// Illustrative positions for the detailed capture-rules page.
//
// Every position here is locked down by `captureExamples.test.ts`, which feeds
// each FEN to the real engine and asserts that `getAllLegalMoves` returns
// EXACTLY the moves listed below — no more, no fewer. If a rule ever changes,
// the test fails instead of the page quietly teaching something false.
//
// FENs are written in the EXPANDED explicit-list form ("W:W30,32:B26,28,18"),
// never with hyphen ranges: `PuzzlePreviewBoard.parseFen` accepts ranges but
// `fenToBoardState` in @draughts/core does not (parseInt('21-32') === 21), and
// the whole point is that the board and the test read the same string.
//
// Square numbering is the variant's own: italian and triestine put the dark
// square bottom-right, english bottom-left, so the same number is a different
// square on different boards. Positions are therefore per-variant, never shared.

import { GameVariant } from '@draughts/shared';

export interface CaptureExampleMove {
  from: number;
  to: number;
  /** Captured squares, in the order the engine reports them. */
  captures: number[];
  promotion?: true;
}

export interface CaptureExample {
  fen: string;
  /** The complete legal move set for this position. */
  moves: CaptureExampleMove[];
  /** Squares to highlight on the diagram: origin, path, captures, landing. */
  mark: number[];
}

export type CaptureExampleId =
  | 'geometry'
  | 'menVsKing'
  | 'multi'
  | 'ladderMaxPieces'
  | 'ladderWithKing'
  | 'ladderMaxKings'
  | 'ladderFirstKing'
  | 'ladderFree'
  | 'promotion';

/**
 * Per-variant example positions. A missing entry simply means no diagram is
 * shown for that section — the page never invents one. English has no
 * precedence ladder, so it only carries `ladderFree`.
 */
export const CAPTURE_EXAMPLES: Partial<
  Record<GameVariant, Partial<Record<CaptureExampleId, CaptureExample>>>
> = {
  [GameVariant.ITALIAN]: {
    // The plain jump: over the adjacent enemy, onto the empty square behind it.
    geometry: {
      fen: 'W:W23:B19',
      moves: [{ from: 23, to: 14, captures: [19] }],
      mark: [14, 19, 23],
    },
    // The man is next to an enemy king and simply cannot take it: the only
    // legal move is a quiet one. The king is a wall.
    menVsKing: {
      fen: 'W:W22:BK18',
      moves: [{ from: 22, to: 19, captures: [] }],
      mark: [19, 22],
    },
    // One legal move, three pieces: stopping after the first jump is not a move.
    multi: {
      fen: 'W:W30:B26,18,11',
      moves: [{ from: 30, to: 7, captures: [26, 18, 11] }],
      mark: [7, 11, 14, 18, 21, 26, 30],
    },
    // 32 could take 28, but 30 takes two. The single capture is illegal.
    ladderMaxPieces: {
      fen: 'W:W30,32:B26,28,18',
      moves: [{ from: 30, to: 14, captures: [26, 18] }],
      mark: [14, 18, 21, 26, 30],
    },
    // King and man each capture exactly one man: the king must be the one to do it.
    ladderWithKing: {
      fen: 'W:WK22,24:B18,20',
      moves: [{ from: 22, to: 13, captures: [18] }],
      mark: [13, 18, 22],
    },
    // Two kings each capture one piece; the one taking the enemy king wins out.
    ladderMaxKings: {
      fen: 'W:WK22,K24:BK18,20',
      moves: [{ from: 22, to: 13, captures: [18] }],
      mark: [13, 18, 22],
    },
    // Both routes take two pieces including one king. Only the route that meets
    // the king on the FIRST jump is legal (19x3, not 19x17).
    ladderFirstKing: {
      fen: 'W:WK19:BK14,6,22,K21',
      moves: [{ from: 19, to: 3, captures: [14, 6] }],
      mark: [3, 6, 10, 14, 19],
    },
    // A closed loop: same four pieces either way round, back to the starting
    // square. Every level ties, so the choice is genuinely free.
    ladderFree: {
      fen: 'W:WK6:B10,11,18,19',
      moves: [
        { from: 6, to: 6, captures: [10, 18, 19, 11] },
        { from: 6, to: 6, captures: [11, 19, 18, 10] },
      ],
      mark: [6, 10, 11, 13, 15, 18, 19, 22],
    },
    // Lands on the last row, promotes, move over — the man on 7 stays put.
    promotion: {
      fen: 'W:W10:B6,7',
      moves: [{ from: 10, to: 3, captures: [6], promotion: true }],
      mark: [3, 6, 10],
    },
  },

  [GameVariant.TRIESTINE]: {
    geometry: {
      fen: 'W:W33:B28',
      moves: [{ from: 33, to: 24, captures: [28] }],
      mark: [24, 28, 33],
    },
    menVsKing: {
      fen: 'W:W27:BK22',
      moves: [{ from: 27, to: 23, captures: [] }],
      mark: [23, 27],
    },
    multi: {
      fen: 'W:W38:B33,22,12',
      moves: [{ from: 38, to: 7, captures: [33, 22, 12] }],
      mark: [7, 12, 16, 22, 27, 33, 38],
    },
    ladderMaxPieces: {
      fen: 'W:W37,39:B32,34,22',
      moves: [{ from: 37, to: 17, captures: [32, 22] }],
      mark: [17, 22, 26, 32, 37],
    },
    ladderWithKing: {
      fen: 'W:WK27,29:B22,24',
      moves: [{ from: 27, to: 16, captures: [22] }],
      mark: [16, 22, 27],
    },
    ladderMaxKings: {
      fen: 'W:WK27,K29:BK22,24',
      moves: [{ from: 27, to: 16, captures: [22] }],
      mark: [16, 22, 27],
    },
    ladderFirstKing: {
      fen: 'W:WK23:BK17,7,27,K26',
      moves: [{ from: 23, to: 3, captures: [17, 7] }],
      mark: [3, 7, 12, 17, 23],
    },
    ladderFree: {
      fen: 'W:WK7:B12,13,22,23',
      moves: [
        { from: 7, to: 7, captures: [12, 22, 23, 13] },
        { from: 7, to: 7, captures: [13, 23, 22, 12] },
      ],
      mark: [7, 12, 13, 16, 18, 22, 23, 27],
    },
    promotion: {
      fen: 'W:W12:B7,8',
      moves: [{ from: 12, to: 3, captures: [7], promotion: true }],
      mark: [3, 7, 12],
    },
  },

  [GameVariant.ENGLISH]: {
    geometry: {
      fen: 'W:W23:B19',
      moves: [{ from: 23, to: 16, captures: [19] }],
      mark: [16, 19, 23],
    },
    // The mirror image of the italian rule: here the man takes the king.
    menVsKing: {
      fen: 'W:W23:BK19',
      moves: [{ from: 23, to: 16, captures: [19] }],
      mark: [16, 19, 23],
    },
    multi: {
      fen: 'W:W30:B26,18',
      moves: [{ from: 30, to: 14, captures: [26, 18] }],
      mark: [14, 18, 23, 26, 30],
    },
    // The whole point of english draughts: one capture takes a single piece,
    // the other takes two, and BOTH are legal. The same position in italian
    // leaves only 23x7.
    ladderFree: {
      fen: 'W:W21,23:B17,19,11',
      moves: [
        { from: 21, to: 14, captures: [17] },
        { from: 23, to: 7, captures: [19, 11] },
      ],
      mark: [7, 11, 14, 16, 17, 19, 21, 23],
    },
    promotion: {
      fen: 'W:W10:B7,8',
      moves: [{ from: 10, to: 3, captures: [7], promotion: true }],
      mark: [3, 7, 10],
    },
  },
};

/** The example for a variant's section, or undefined when none is published. */
export function captureExample(
  variant: GameVariant,
  id: CaptureExampleId | undefined,
): CaptureExample | undefined {
  if (!id) return undefined;
  return CAPTURE_EXAMPLES[variant]?.[id];
}
